import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC, s_PL } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Show 5 words for 3 seconds, then pick them from 10 options (5 correct + 5 distractors).
// 4 rounds. Score = total correct out of 20. Higher = better.
const WORD_POOL_EN = ['apple','river','cloud','bridge','pencil','winter','castle','forest','mirror','rocket','candle','sunset','guitar','pillow','desert','market','bottle','garden','lantern','shadow','anchor','stream','whisper','silver','copper'];
const WORD_POOL_NL = ['appel','rivier','wolk','brug','potlood','winter','kasteel','bos','spiegel','raket','kaars','zonsondergang','gitaar','kussen','woestijn','markt','fles','tuin','lantaarn','schaduw','anker','beek','fluister','zilver','koper'];

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function WordMemoryGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const ROUNDS = 4;
  const SHOW_COUNT = 5;
  const POOL = nl ? WORD_POOL_NL : WORD_POOL_EN;

  const [phase, setPhase]           = useState('idle'); // idle|memorise|recall|roundDone
  const [targets, setTargets]       = useState([]);
  const [choices, setChoices]       = useState([]);
  const [selected, setSelected]     = useState([]);
  const [round, setRound]           = useState(0);
  const [score, setScore]           = useState(0);
  const [countdown, setCountdown]   = useState(3);
  const [finished, setFinished]     = useState(false);
  const [best, setBest]             = useStorage('gm_wordmemory_best', 0);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const timerRef  = useRef(null);
  const scoreRef  = useRef(0);
  const usedRef   = useRef([]);

  const startRound = (r) => {
    setRound(r);
    // pick 5 target words not yet used
    const available = POOL.filter(w => !usedRef.current.includes(w));
    const tgts = shuffle(available).slice(0, SHOW_COUNT);
    usedRef.current = [...usedRef.current, ...tgts];
    // pick 5 distractors
    const distractors = shuffle(POOL.filter(w => !tgts.includes(w))).slice(0, SHOW_COUNT);
    setTargets(tgts);
    setChoices(shuffle([...tgts, ...distractors]));
    setSelected([]);
    setCountdown(3);
    setPhase('memorise');
    // countdown
    let c = 3;
    timerRef.current = setInterval(() => {
      c--;
      setCountdown(c);
      if (c <= 0) { clearInterval(timerRef.current); setPhase('recall'); }
    }, 1000);
  };

  const start = () => {
    scoreRef.current = 0; usedRef.current = [];
    setScore(0); setFinished(false); setShowResults(null);
    startRound(1);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    if (finished) {
      const final = scoreRef.current;
      if (final > best) setBest(final);
      saveGameScore('wordmemory', final);
      if (partner) setShowResults({ myScore: final, partnerScore: Math.floor(Math.random() * 8) + 10 });
    }
  }, [finished]); // eslint-disable-line

  const toggleWord = (w) => {
    if (phase !== 'recall') return;
    setSelected(prev => prev.includes(w) ? prev.filter(x => x !== w) : [...prev, w]);
  };

  const confirm = () => {
    const correct = selected.filter(w => targets.includes(w)).length;
    const wrong   = selected.filter(w => !targets.includes(w)).length;
    const net     = Math.max(0, correct - wrong);
    scoreRef.current += net;
    setScore(s => s + net);
    setPhase('roundDone');

    if (round >= ROUNDS) { setTimeout(() => setFinished(true), 800); }
    else { setTimeout(() => startRound(round + 1), 1000); }
  };

  const maxScore = ROUNDS * SHOW_COUNT;

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel={`/${maxScore}`} partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel={`/${maxScore}`} aiComment={nl ? `Score van ${showResults.myScore}/${maxScore}! Word Memory traint je werkgeheugen en woordoproeping.` : `Score of ${showResults.myScore}/${maxScore}! Word Memory trains working memory and word recall.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Word Memory" icon="📝" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>■ {nl ? 'Stop' : 'Stop'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {phase === 'idle' && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>📝</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Woordgeheugen' : 'Word Memory'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}>
            {nl ? `${SHOW_COUNT} woorden worden 3 seconden getoond. Onthoud ze en vind ze terug! ${ROUNDS} rondes.` : `${SHOW_COUNT} words are shown for 3 seconds. Memorise them and find them again! ${ROUNDS} rounds.`}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {best}/{maxScore}</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {phase === 'memorise' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 11, color: _T.accent, letterSpacing: 3, fontWeight: 700, marginBottom: 12 }}>{nl ? 'ONTHOUD DEZE WOORDEN' : 'MEMORISE THESE WORDS'}</div>
          <div style={{ marginBottom: 20 }}>
            {targets.map(w => (
              <div key={w} style={{ fontSize: 22, color: C.white, fontWeight: 700, padding: '6px 0', fontFamily: 'Georgia,serif' }}>{w}</div>
            ))}
          </div>
          <div style={{ fontSize: 36, color: _T.accent, fontWeight: 700 }}>{countdown}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>{nl ? 'seconden...' : 'seconds...'}</div>
        </div>
      )}

      {phase === 'recall' && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDE' : 'ROUND'}</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{round}/{ROUNDS}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'GESELECTEERD' : 'SELECTED'}</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{selected.length}/{SHOW_COUNT}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>SCORE</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{score}</div></div>
          </div>
          <div style={{ ...s_GC({ padding: 14, marginBottom: 12 }) }}>
            <div style={{ fontSize: 11, color: C.silver, marginBottom: 10 }}>{nl ? `Selecteer de ${SHOW_COUNT} woorden die je zag:` : `Select the ${SHOW_COUNT} words you saw:`}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {choices.map(w => (
                <button key={w} onClick={() => toggleWord(w)} style={{ ...s_PL(selected.includes(w), _T.accent), fontSize: 13, padding: '8px 14px' }}>
                  {w}
                </button>
              ))}
            </div>
          </div>
          <button onClick={confirm} style={{ ...s_PB({ width: '100%' }), opacity: selected.length > 0 ? 1 : 0.4 }} disabled={selected.length === 0}>
            {nl ? 'Bevestigen →' : 'Confirm →'}
          </button>
        </>
      )}

      {phase === 'roundDone' && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>{score > round * (SHOW_COUNT * 0.6) ? '✅' : '🔄'}</div>
          <div style={{ color: C.teal, fontSize: 18, fontWeight: 700 }}>{score}/{round * SHOW_COUNT}</div>
          <div style={{ color: C.silver, fontSize: 12, marginTop: 4 }}>{nl ? 'Volgende ronde...' : 'Next round...'}</div>
        </div>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{score}<span style={{ fontSize: 14, color: C.silver }}>/{maxScore}</span></div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'woorden herinnerd' : 'words recalled'}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default WordMemoryGame;
